/**
 * Customer-Order service for Shopify operations
 * Handles all customer and order-related API calls with comprehensive validation
 */

import { BaseShopifyService } from "../services/base";
import {
  GET_ORDER_HISTORY,
  GET_ORDER_BY_ID,
  GET_CUSTOMER_PROFILE,
} from "./queries";
import {
  mapGetOrderHistoryToResponse,
  mapGetOrderByIdToResponse,
  mapGetCustomerProfileToResponse,
} from "./mappers";

import { IOptionsRequest, IResponse } from "../../../models";
import { formatResponse } from "../../../core/utils";

export class CustomerOrderService extends BaseShopifyService {
  /**
   * Get order history for a customer
   */
  async getOrderHistory(
    customerAccessToken: string,
    page = 1,
    limit = 10,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Use custom query from options if provided, otherwise use default
      const query = options?.query || GET_ORDER_HISTORY;

      // Merge params with custom variables from options
      const variables = {
        customerAccessToken,
        first: limit,
        after:
          page > 1
            ? btoa(`arrayconnection:${(page - 1) * limit - 1}`)
            : undefined,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_ORDER_HISTORY", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch order history due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.customer) {
        return formatResponse({
          success: true,
          message:
            "Request completed successfully. Customer not found or invalid access token.",
          data: null,
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Order history retrieved successfully.",
        data: mapGetOrderHistoryToResponse(result.data.customer),
        meta: {
          pagination: result.data.customer.orders.pageInfo,
        },
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getOrderHistory", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get a single order by ID
   */
  async getOrderById(
    customerAccessToken: string,
    orderId: string,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Use custom query from options if provided, otherwise use default
      const query = options?.query || GET_ORDER_BY_ID;

      // Merge params with custom variables from options
      const variables = {
        customerAccessToken,
        orderId: orderId.includes("gid://")
          ? orderId
          : `gid://shopify/Order/${orderId}`,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_ORDER_BY_ID", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch order due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (
        !result?.data ||
        !result.data.customer ||
        !result.data.customer.order
      ) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. Order not found.",
          data: null,
        });
      }

      // ✅ Normal success path
      const order = mapGetOrderByIdToResponse(result.data.customer.order);

      return formatResponse({
        success: true,
        message: "Order retrieved successfully.",
        data: order,
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getOrderById", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get customer profile with enhanced parameter support
   * Supports custom query and variables through options
   */
  async getCustomerProfile(
    customerAccessToken: string,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Use custom query from options if provided, otherwise use default
      const query = options?.query || GET_CUSTOMER_PROFILE;

      // Merge params with custom variables from options
      const variables = {
        customerAccessToken,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_CUSTOMER_PROFILE", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch customer profile due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.customer) {
        return formatResponse({
          success: true,
          message:
            "Request completed successfully. Customer not found or invalid access token.",
          data: null,
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Customer profile retrieved successfully.",
        data: mapGetCustomerProfileToResponse(result.data.customer),
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getCustomerProfile", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }
}
